import { useEffect, useState } from 'react';
import { useIcibAdmin } from '../context/IcibAdminContext';
import { useToast } from '../../toast/ToastContext';
import Pagination, { usePagination } from '../components/Pagination';
import { fmtDT } from '../utils/format';

const STATUS_FILTERS = ['all', 'pending', 'processed', 'failed', 'cancelled'];
const ACTION_FILTERS = ['all', 'ADDITION', 'UPDATE', 'REMOVAL'];

function productIds(v) {
  if (Array.isArray(v)) return v;
  return v ? [v] : [];
}

/* ICIB-13 — View and cancel upcoming Additions, Updates, and Removals.
   Added alongside §2.6a's effective_date-deferred intake (see IntakeView):
   an Addition/Update/Removal row submitted with a future effective_date
   isn't applied at all yet — it's held as a PendingIntakeRow until a daily
   sweep re-validates and applies it on the day itself. Without this view
   a scheduled row would be invisible and unpullable-back from the moment
   it's submitted until its date arrives. Two operations: list every row
   for the group regardless of status (ICIB-13a), and cancel one still-
   `pending` row before its date arrives (ICIB-13b) — `processed`/`failed`
   rows already reflect something that happened, so only `pending` ones
   can be cancelled. Not owned by any one ICIB-02 story in CLAUDE.md's
   table — it's the read/cancel side of the effective_date field every
   Intake action now submits, so it lives next to Intake in the nav.
   Each row echoes `subsidised_product_level` as submitted (ICIB-13a,
   §2.8): an id array from a bulk row (ICIB-174), a single id string from
   the single-item Addition, or null on an UPDATE / single-item DELETE
   Removal — productIds() below flattens all three for the Products
   column.
   Additions also wait on eligibility_days (§2.6a): a product is only
   enrolled once effective_date + its eligibility_days arrives, so an
   Addition is held one row per product, each with its own `apply_on` —
   the date the sweep actually applies the row (just effective_date for an
   Update/Removal). The server orders rows soonest-apply_on-first. The
   effective_date as submitted is shown alongside it, labelled just
   "Effective date" since it means employment effective date on an
   Addition, the correction date on an Update, and removal effective date
   on a Removal — for an Update/Removal it matches apply_on, while for an
   Addition apply_on is later by the product's eligibility_days. A
   Removal for someone whose Addition was still waiting cancels that row
   too, so `cancelled` doesn't always mean an admin cancelled it here. */
function PendingIntakeView() {
  const {
    status, groupId, curGroup, pendingIntake, pendingIntakeStatus, pendingIntakeError, loadPendingIntake, cancelPendingIntake, product,
  } = useIcibAdmin();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { loadPendingIntake(groupId); }, [groupId, loadPendingIntake]);

  const rows = pendingIntake
    .filter((r) => statusFilter === 'all' || r.status === statusFilter)
    .filter((r) => actionFilter === 'all' || r.action === actionFilter)
    .filter((r) => {
      // Rows carry only an email to identify the person (no name), so
      // search matches that plus the product names shown in the table.
      const needle = q.trim().toLowerCase();
      if (!needle) return true;
      const products = productIds(r.subsidised_product_level).map((id) => product(id)?.name || id).join(' ');
      return `${r.email || ''} ${products}`.toLowerCase().includes(needle);
    });
  const { page, setPage, pageCount, pageItems: pagedRows, total } = usePagination(rows);

  async function onCancel(row) {
    setBusyId(row.pending_intake_row_id);
    const res = await cancelPendingIntake(groupId, row.pending_intake_row_id);
    setBusyId(null);
    if (res?.error) toast(res.message || 'Could not cancel this row.', 'bad', res.error);
    else toast('Cancelled.', 'ok');
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Scheduled enrolment</div>
      <div className="page-h"><h2>Pending enrolment</h2></div>
      <p className="page-sub">
        Additions, updates and removals for {curGroup()?.name || 'this company'} that apply on a future
        date — a future effective date, or for an addition, a product whose eligibility period hasn't
        passed yet. Nothing happens until the "applies on" date, and a still-upcoming row can be
        pulled back first.
      </p>

      {pendingIntakeError && <div className="note bad" style={{ marginBottom: 14 }}><b>Couldn't load pending enrolment</b>{pendingIntakeError}</div>}

      {!groupId ? (
        <div className="card"><div className="empty"><b>Select a company</b>Choose a company from the scope selector above.</div></div>
      ) : (
        <>
          <div className="filterbar">
            <input type="search" placeholder="Search email or product…" value={q} onChange={(e) => setQ(e.target.value)} />
            {STATUS_FILTERS.map((s) => (
              <span key={s} className="chip" aria-pressed={statusFilter === s} onClick={() => setStatusFilter(s)} role="button" tabIndex={0}>
                {s === 'all' ? 'All' : s}
              </span>
            ))}
            <span className="spacer" style={{ flex: 1 }} />
            {ACTION_FILTERS.map((a) => (
              <span key={a} className="chip" aria-pressed={actionFilter === a} onClick={() => setActionFilter(a)} role="button" tabIndex={0}>
                {a === 'all' ? 'All actions' : a}
              </span>
            ))}
            <span className="ep">GET /companies/{'{company_id}'}/pending-intake</span>
          </div>

          <div className="card">
            {pendingIntakeStatus === 'loading' || pendingIntakeStatus === 'idle' ? (
              <div className="empty"><b>Loading…</b></div>
            ) : rows.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Action</th><th>Person</th><th>Products</th><th>Applies on</th><th>Status</th><th>Effective date</th><th></th></tr></thead>
                  <tbody>
                    {pagedRows.map((r) => (
                      <tr key={r.pending_intake_row_id}>
                        <td><span className="tag navy">{r.action}</span></td>
                        <td><span className="strong">{r.email}</span></td>
                        <td>
                          {productIds(r.subsidised_product_level).length
                            ? productIds(r.subsidised_product_level).map((id) => product(id)?.name || id).join(', ')
                            : <span className="dim">—</span>}
                        </td>
                        <td className="mono nowrap">{r.apply_on || r.effective_date}</td>
                        <td>
                          <span className={'tag' + (r.status === 'processed' ? ' ok' : r.status === 'failed' ? ' bad' : r.status === 'cancelled' ? ' off' : ' warn')}>{r.status}</span>
                          {r.status === 'failed' && r.error_message && <div className="dim" style={{ marginTop: 4, fontSize: 11.5 }}>{r.error_message}</div>}
                          {r.status === 'processed' && r.processed_at && <div className="dim" style={{ marginTop: 4, fontSize: 11.5 }}>{fmtDT(r.processed_at)}</div>}
                          {r.status === 'cancelled' && r.cancelled_at && <div className="dim" style={{ marginTop: 4, fontSize: 11.5 }}>{fmtDT(r.cancelled_at)}</div>}
                        </td>
                        <td className="mono nowrap">{r.effective_date || '—'}</td>
                        <td>
                          {r.status === 'pending' && (
                            <button className="btn sm danger" disabled={busyId === r.pending_intake_row_id} onClick={() => onCancel(r)}>
                              {busyId === r.pending_intake_row_id ? '…' : 'Cancel'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="empty"><b>Nothing here</b>{q.trim() ? 'Try a different search.' : 'No scheduled intake matches this filter for this company.'}</div>}
            <Pagination page={page} setPage={setPage} pageCount={pageCount} total={total} />
          </div>
        </>
      )}
    </>
  );
}

export default PendingIntakeView;
